// Stage 5 C-A §14 Actions + §15 Requested Information.
//
// §14 Actions: overdue-required → required → optional; completed as
//     history.  NO universal Mark Complete — each action's completion
//     runs through the owning workflow's legitimate completion path.
// §15 RFI: structured items with autosave; Not Available only where
//     the specific item permits.  Formal submission requires all
//     required items resolved.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { portalApi, fmtDate, daysUntil } from "./portalApi";
import {
    PageHeader, Card, CardHeader, Pill, Button, EmptyState, Spinner,
    Breadcrumbs, AutosaveIndicator, AccessState,
} from "./PortalUi";
import { usePageMeta } from "../../components/Layout";

function actionRoute(a) {
    // §14 — Actions route into their OWN workflow's structured
    // completion, not a generic completion API on the Actions page.
    const t = a.linked_source_type;
    const kind = a.kind || a.action_type;
    if (t === "information_request" && a.linked_source_id) return `/portal/rfis/${a.linked_source_id}`;
    if (t === "design_issue" && a.linked_source_id) return `/portal/design/issues/${a.linked_source_id}`;
    if (kind === "rfi" && a.linked_id) return `/portal/rfis/${a.linked_id}`;
    if ((kind === "design_feedback" || kind === "feedback_request") && a.linked_id) return `/portal/design/issues/${a.linked_id}`;
    if (kind === "sign_agreement") return "/portal/files";
    return null;
}

function actionKindLabel(k) {
    const map = {
        "rfi": "Requested information",
        "information_request": "Requested information",
        "design_feedback": "Design feedback",
        "feedback_request": "Design feedback",
        "sign_agreement": "Sign agreement",
        "review_document": "Review document",
    };
    return map[k] || (k ? k.replace(/_/g, " ").replace(/^./, c => c.toUpperCase()) : "Action");
}

export function PortalActions() {
    usePageMeta({ title: "Actions | Studio G4 Portal" });
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        portalApi.actions().then(r => setData(r.data))
            .catch(e => setErr(e?.response?.data?.detail || "Unavailable"));
    }, []);
    if (err) return <div className="py-8 px-4"><EmptyState heading="Actions unavailable" note={err} testid="portal-actions-err" /></div>;
    if (!data) return <Spinner testid="portal-actions-loading" />;

    const outstanding = data.outstanding || [];
    const completed = data.completed || [];
    const overdueCount = outstanding.filter(a => a.required && a.due_at && new Date(a.due_at) < new Date()).length;

    return (
        <div className="py-8 px-4" data-testid="portal-actions">
            <PageHeader kicker="Your project" title="Actions"
                right={outstanding.length > 0 && (
                    <div className="flex items-center gap-2">
                        {overdueCount > 0 && <Pill tone="danger" testid="portal-actions-overdue-count">{overdueCount} overdue</Pill>}
                        <Pill tone="accent">{outstanding.length} outstanding</Pill>
                    </div>
                )}
                testid="portal-actions-header" />

            {outstanding.length === 0 ? (
                <EmptyState heading="No current actions"
                    note="You're all up to date. When your project team needs input from you, it will show here."
                    testid="portal-actions-empty" />
            ) : (
                <Card testid="portal-actions-outstanding">
                    <CardHeader title="Awaiting your input" subtle />
                    <div className="divide-y divide-[#EEEBE4]">
                        {outstanding.map(a => {
                            const overdue = a.required && a.due_at && new Date(a.due_at) < new Date();
                            const route = actionRoute(a);
                            return (
                                <div key={a.id} className="px-5 py-3 flex items-center justify-between gap-3"
                                     data-testid={`portal-action-row-${a.id}`}>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            {overdue && <Pill tone="danger">Overdue</Pill>}
                                            <div className="font-manrope text-[14px] text-charcoal">{a.title}</div>
                                        </div>
                                        <div className="text-[11.5px] text-[#8A8A84] mt-1 flex items-center gap-3">
                                            <span>{a.required ? "Required" : "Optional"}</span>
                                            <span>·</span>
                                            <span>{actionKindLabel(a.kind || a.action_type)}</span>
                                            {a.due_at && (<><span>·</span><span>Due {fmtDate(a.due_at)}</span></>)}
                                        </div>
                                    </div>
                                    {route && (
                                        <Link to={route}>
                                            <Button variant="primary" testid={`portal-action-open-${a.id}`}>Open →</Button>
                                        </Link>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Card>
            )}

            {completed.length > 0 && (
                <div className="mt-6">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8A84] font-manrope font-semibold mb-2">Completed history</div>
                    <Card testid="portal-actions-completed">
                        <div className="divide-y divide-[#EEEBE4]">
                            {completed.slice(0, 20).map(a => (
                                <div key={a.id} className="px-5 py-2.5 flex items-center justify-between gap-3"
                                     data-testid={`portal-action-completed-${a.id}`}>
                                    <div className="min-w-0">
                                        <div className="font-manrope text-[13px] text-charcoal">{a.title}</div>
                                        <div className="text-[11px] text-[#8A8A84] mt-0.5">Completed {fmtDate(a.completed_at)}</div>
                                    </div>
                                    <Pill tone="ok">Done</Pill>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}

// ---------- RFI detail (§15) ----------

export function PortalRfiDetail() {
    const { rfiId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [responses, setResponses] = useState({});
    const [saveState, setSaveState] = useState("idle");
    const [savedAt, setSavedAt] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirmSubmit, setConfirmSubmit] = useState(false);
    const [submitted, setSubmitted] = useState(null);
    const saveTimer = useRef(null);
    const bootRef = useRef(false);
    const rfiLabel = data?.rfi?.reference || data?.rfi?.rfi_number || "RFI";
    usePageMeta({ title: `${rfiLabel} · Actions | Studio G4 Portal` });

    useEffect(() => {
        portalApi.rfiDetail(rfiId).then(r => {
            setData(r.data);
            const d = r.data.draft || {};
            if (d.responses) setResponses(d.responses);
            if (d.state === "submitted") setSubmitted({ at: d.submitted_at, id: d.submission_id });
            // Also — if the RFI itself is already completed on the
            // server, treat as read-only.
            if (r.data.rfi?.status === "completed") setSubmitted({ at: r.data.rfi.completed_at });
            bootRef.current = true;
        }).catch(e => {
            if (e?.response?.status === 404) setErr("not_found");
            else setErr(e?.response?.data?.detail || "Unavailable");
        });
    }, [rfiId]);

    useEffect(() => {
        if (!bootRef.current || submitted) return;
        setSaveState("saving");
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            try {
                await portalApi.rfiAutosave(rfiId, responses);
                setSaveState("saved"); setSavedAt(Date.now());
            } catch { setSaveState("error"); }
        }, 700);
        return () => saveTimer.current && clearTimeout(saveTimer.current);
    }, [responses, rfiId, submitted]);

    const items = (data?.items) || [];
    const rfi = data?.rfi;

    const requiredOutstanding = useMemo(() => {
        return items.filter(it => {
            if (!it.required) return false;
            const r = responses[it.id] || {};
            const hasResp = !!r.note || !!r.not_available || (it.response_files || []).length > 0;
            return !hasResp;
        });
    }, [items, responses]);

    function setItem(id, patch) {
        setResponses(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
    }
    async function submit() {
        setSubmitting(true);
        try {
            const r = await portalApi.rfiSubmit(rfiId, responses);
            setSubmitted({ at: r.data.submitted_at, id: r.data.submission_id, status: r.data.status, message: r.data.message });
            setConfirmSubmit(false);
        } catch (e) {
            alert(e?.response?.data?.detail || "Submission failed");
        } finally { setSubmitting(false); }
    }

    if (err === "not_found") return <div className="py-8 px-4"><AccessState kind="unavailable" testid="portal-rfi-notfound" /></div>;
    if (err) return <div className="py-8 px-4"><AccessState kind="temporary" note={err} testid="portal-rfi-err" /></div>;
    if (!data) return <Spinner testid="portal-rfi-loading" />;

    const readOnly = !!submitted;
    return (
        <div className="py-8 px-4" data-testid="portal-rfi-detail">
            <Breadcrumbs items={[{ label: "Actions", to: "/portal/actions" }, { label: rfi.title }]} />
            <PageHeader kicker="Information request" title={rfi.title}
                right={
                    <div className="flex items-center gap-2">
                        {rfi.due_at && <span className="text-[11.5px] text-[#6B6B67]">Due {fmtDate(rfi.due_at)}</span>}
                        {!readOnly && <AutosaveIndicator state={saveState} savedAt={savedAt} testid="portal-rfi-autosave-indicator" />}
                    </div>
                }
                testid="portal-rfi-header" />

            {rfi.intro && (
                <Card className="mb-5"><div className="p-4 text-[13.5px] text-[#4A4A46] leading-relaxed">{rfi.intro}</div></Card>
            )}

            {submitted && (
                submitted.status === "recorded_pending_processing" ? (
                <div className="mb-4 border border-[#E9D084] bg-[#FFF9E6] rounded-md px-4 py-3 text-[13px] text-[#5C4A16] font-manrope"
                     data-testid="portal-rfi-submitted-pending-banner">
                    <div className="font-semibold mb-1">Received — being finalised</div>
                    {submitted.message || "Your response has been received. We're finalising a few things at our end — please do not submit it again."}
                </div>
                ) : (
                <div className="mb-4 border border-[#C7DFC7] bg-[#EEF6EE] rounded-md px-4 py-3 text-[13px] text-[#2E5A2E] font-manrope"
                     data-testid="portal-rfi-submitted-banner">
                    {submitted.message || "Your response has been received."} Submitted {fmtDate(submitted.at)}.
                </div>
                )
            )}

            <div className="space-y-3">
                {items.map(it => {
                    const r = responses[it.id] || {};
                    const answered = !!r.note || !!r.not_available || (it.response_files || []).length > 0;
                    return (
                        <Card key={it.id} testid={`portal-rfi-item-${it.id}`}>
                            <div className="p-4">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-manrope text-[14px] text-charcoal">{it.prompt}</div>
                                        <div className="text-[11.5px] text-[#8A8A84] mt-0.5 uppercase tracking-[0.06em]">
                                            {it.required ? "Required" : "Optional"} · {it.response_type}
                                        </div>
                                    </div>
                                    <div>
                                        {answered ? (
                                            r.not_available ? <Pill tone="warn">Not available</Pill> : <Pill tone="ok">Answered</Pill>
                                        ) : (
                                            <Pill tone="neutral">Outstanding</Pill>
                                        )}
                                    </div>
                                </div>

                                {!readOnly && (
                                    <div className="mt-3 space-y-2">
                                        <textarea disabled={r.not_available} rows={2} placeholder="Your response…"
                                            value={r.note || ""} onChange={e => setItem(it.id, { note: e.target.value })}
                                            data-testid={`portal-rfi-item-${it.id}-note`}
                                            className="w-full px-2 py-1.5 border border-[#D7D4CC] rounded text-[13.5px] disabled:bg-[#F7F5EF]" />
                                        {it.allow_not_available && (
                                            <label className="flex items-center gap-2 text-[12px] text-[#4A4A46]">
                                                <input type="checkbox" checked={!!r.not_available}
                                                    onChange={e => setItem(it.id, { not_available: e.target.checked, note: e.target.checked ? "" : r.note })}
                                                    data-testid={`portal-rfi-item-${it.id}-na`} className="w-4 h-4" />
                                                <span>Mark as Not Available</span>
                                            </label>
                                        )}
                                        <div className="mt-2">
                                            <label className="inline-flex items-center gap-2 text-[12px] text-[#4A4A46] cursor-pointer">
                                                <input type="file" className="hidden"
                                                    onChange={async e => {
                                                        const f = e.target.files && e.target.files[0];
                                                        if (!f) return;
                                                        try {
                                                            await portalApi.rfiItemUpload(rfi.id, it.id, f);
                                                            const rr = await portalApi.rfiDetail(rfi.id);
                                                            setData(rr.data);
                                                        } catch (err) {
                                                            alert(err?.response?.data?.detail || "Upload failed");
                                                        }
                                                    }}
                                                    data-testid={`portal-rfi-item-${it.id}-upload-input`}
                                                />
                                                <span className="px-3 py-1 rounded bg-white border border-[#D7D4CC] text-[12px] font-manrope font-semibold text-[#4A4A46] hover:bg-[#F7F5EF]" data-testid={`portal-rfi-item-${it.id}-upload-btn`}>
                                                    {(it.response_files || []).length ? "Replace file" : "Upload file"}
                                                </span>
                                                {(it.response_files || [])[0] && (
                                                    <span className="text-[11.5px] text-[#6B6B67]">{it.response_files[0].filename} · v{it.response_files[0].version_number}</span>
                                                )}
                                            </label>
                                        </div>
                                    </div>
                                )}
                                {readOnly && r.note && (
                                    <div className="mt-3 p-3 bg-[#FAF7F1] rounded text-[13px] text-[#4A4A46]">{r.note}</div>
                                )}
                            </div>
                        </Card>
                    );
                })}
            </div>

            {!readOnly && (
                <div className="mt-6 flex items-center justify-between gap-4">
                    <div className="text-[12px] text-[#6B6B67]">
                        {requiredOutstanding.length === 0 ? (
                            <>All required items are answered.</>
                        ) : (
                            <>{requiredOutstanding.length} required item{requiredOutstanding.length === 1 ? "" : "s"} outstanding.</>
                        )}
                    </div>
                    <Button variant="primary" disabled={requiredOutstanding.length > 0}
                        onClick={() => setConfirmSubmit(true)} testid="portal-rfi-submit-btn">
                        Submit requested information
                    </Button>
                </div>
            )}

            {confirmSubmit && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="portal-rfi-confirm-submit">
                    <div className="bg-white rounded-md max-w-md w-full p-6">
                        <h2 className="font-manrope text-[17px] font-semibold text-charcoal mb-2">Submit information</h2>
                        <p className="text-[13.5px] text-[#4A4A46] leading-relaxed mb-5">
                            Your responses will be sent to your project team. You can request a clarification later if you need to add anything after this.
                        </p>
                        <div className="flex items-center justify-end gap-3">
                            <Button variant="secondary" onClick={() => setConfirmSubmit(false)} testid="portal-rfi-confirm-cancel">Cancel</Button>
                            <Button variant="primary" onClick={submit} disabled={submitting} testid="portal-rfi-confirm-submit-btn">
                                {submitting ? "Submitting…" : "Submit"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
